'use client'

import { BannerDisplayType, WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import NAVIGATION_TYPE from '@src/quince-site-common/src/constants/navigationType'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import HeroBanner from '@src/SectionRenderer/sections/heroBanner'
import HeroCarousel from '@src/SectionRenderer/sections/heroCarousel/HeroCarousel'
import { MultipleHeroBanners } from '@src/SectionRenderer/sections/MultipleHeroBanners'

import { buildHeroBannerConfig, hasBannerContent, makeSection } from '../banner-section-builders'
import { buildWidgetTrackingMetaData } from '../widget-utils'

import type {
  CollectionBannerWidgetData,
  HomepageBannerWidgetData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

function toNavigationType(navigationType?: string): NAVIGATION_TYPE | null {
  if (navigationType && navigationType in NAVIGATION_TYPE) {
    return NAVIGATION_TYPE[navigationType as keyof typeof NAVIGATION_TYPE]
  }
  return null
}

/**
 * Build the parent container `CustomSection` (HERO_CAROUSEL / MULTIPLE_HERO_BANNERS) whose `subSections`
 * are the per-slide hero banners. Slides reuse the shared `buildHeroBannerConfig` (same conversion the
 * COLLECTION_BANNER widget uses); `makeSection` supplies the class-name/array defaults. The existing
 * container components arrange the sub-banners and own per-slide analytics (horizontalPosition) +
 * DS/legacy routing — same contract the BFF home page used.
 */
function buildContainerSection(
  data: HomepageBannerWidgetData,
  sectionType: CUSTOM_SECTION_TYPE,
  slides: CollectionBannerWidgetData[],
): CustomSection {
  return makeSection({
    id: data.id,
    displayTitle: data.displayTitle,
    title: data.displayTitle,
    sectionType,
    subSections: slides.map(buildHeroBannerConfig),
    navigationType: toNavigationType(data.navigationType),
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics),
  })
}

const HomepageBannerWidgetComponent = (props: PLTypedWidgetProps<HomepageBannerWidgetData>) => {
  const bannerData = props.data.homepageBannerData
  if (!bannerData) return null

  const slides = (Array.isArray(bannerData.slides) ? bannerData.slides : []).filter(hasBannerContent)
  const [firstSlide] = slides
  if (!firstSlide) return null

  switch (bannerData.displayType) {
    case BannerDisplayType.BANNER_DISPLAY_CAROUSEL:
      return (
        <HeroCarousel sectionConfig={buildContainerSection(bannerData, CUSTOM_SECTION_TYPE.HERO_CAROUSEL, slides)} />
      )
    case BannerDisplayType.BANNER_DISPLAY_MULTIPLE:
      return (
        <MultipleHeroBanners
          sectionConfig={buildContainerSection(bannerData, CUSTOM_SECTION_TYPE.MULTIPLE_HERO_BANNERS, slides)}
        />
      )
    case BannerDisplayType.BANNER_DISPLAY_SINGLE:
    case BannerDisplayType.BANNER_DISPLAY_TYPE_UNSPECIFIED:
    case BannerDisplayType.UNRECOGNIZED:
    default:
      // SINGLE (and any unexpected value): render the first slide as a standalone hero banner,
      // identical to how the COLLECTION_BANNER widget renders a single HERO_BANNER.
      return <HeroBanner sectionConfig={buildHeroBannerConfig(firstSlide)} />
  }
}

export const HomepageBannerWidget: PLTypedWidgetInterface<HomepageBannerWidgetData, WidgetType.HOMEPAGE_BANNER> = {
  id: WidgetType.HOMEPAGE_BANNER,
  errorBoundary: PLBaseErrorBoundary,
  render: HomepageBannerWidgetComponent,
}
